import React from "react";
import { GoGraph, GoTable } from "react-icons/go";
import { FiInfo, FiTruck } from "react-icons/fi";
import LedgerBlock from "./LedgerBlock";
import RateTable from "./RateTable";
import {
  periodo,
  resumo,
  colunaEsquerda,
  colunaCentral,
  colunaDireita,
  entradasPorCfop,
} from "../mockData";

function formatCurrency(num, compact = false) {
  if (num === null || num === undefined) return "\u2014";
  const abs = Math.abs(num);
  if (compact) {
    if (abs >= 1000000) return `R$ ${(num / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `R$ ${(num / 1000).toFixed(0)}K`;
  }
  return `R$ ${num.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;
}

/**
 * Distribui os blocos em N colunas usando bin-packing (sempre adiciona o
 * próximo bloco à coluna com menor "peso" acumulado — peso = nº de linhas +
 * cabeçalho). Produz colunas com altura total bem próxima entre si, para
 * que o mosaico fique naturalmente equilibrado antes de aplicar o
 * esticamento da última ficha de cada coluna (ver .ledger-block.is-fill).
 */
function distribuirEmColunas(blocos, numColunas) {
  const colunas = Array.from({ length: numColunas }, () => ({ blocos: [], peso: 0 }));
  blocos.forEach((bloco) => {
    const peso = bloco.linhas.length + 1;
    const destino = colunas.reduce((min, col) => (col.peso < min.peso ? col : min), colunas[0]);
    destino.blocos.push(bloco);
    destino.peso += peso;
  });
  return colunas.map((col) => col.blocos);
}

export default function ImpostosTab() {
  const todosBlocos = [...colunaEsquerda, ...colunaCentral, ...colunaDireita];
  const colunasMosaico = distribuirEmColunas(todosBlocos, 3);

  return (
    <>
      {/* ===== Resumo — valores literais da planilha (Faturamento, Resultado, A Pagar) ===== */}
      <div className="section-title">
        <GoGraph />
        <h4>Resumo do Período</h4>
      </div>
      <div className="kpi-cards hero">
        <div className="kpi-card">
          <div className="card-content">
            <div className="text">
              <p className="kpi-value">{formatCurrency(resumo.faturamentoBruto, true)}</p>
              <p className="kpi-label">Faturamento Bruto</p>
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="card-content">
            <div className="text">
              <p className="kpi-value">{formatCurrency(resumo.faturamentoLiquido, true)}</p>
              <p className="kpi-label">Faturamento Líquido</p>
            </div>
          </div>
        </div>
        <div className="kpi-card success">
          <div className="card-content">
            <div className="text">
              <p className="kpi-value">{formatCurrency(resumo.resultadoCreditoCompra, true)}</p>
              <p className="kpi-label">Resultado — Créditos + Compra Revenda</p>
            </div>
          </div>
        </div>
        <div className="kpi-card danger">
          <div className="card-content">
            <div className="text">
              <p className="kpi-value">{formatCurrency(resumo.totalAPagar, true)}</p>
              <p className="kpi-label">Total de Tributos a Pagar (Apuração)</p>
            </div>
          </div>
        </div>
      </div>

      <div className="info-banner">
        <div className="banner-icon"><FiInfo /></div>
        <div className="banner-text">
          <strong>Layout fiel à planilha.</strong>
          <span> Os três blocos abaixo reproduzem, bloco a bloco e linha a linha, exatamente a disposição e os valores da aba "IMPOSTOS" da planilha de movimentações do PLCom ({periodo}).</span>
        </div>
      </div>

      {/* ===== Mosaico de blocos — mesmo conteúdo da planilha (colunas B:D / F:H /
          J:L), redistribuído em 3 colunas por peso (bin-packing) para equilibrar
          a altura. A última ficha de cada coluna "cresce" (is-fill) até a borda
          inferior comum, preenchendo 100% da altura da seção sem vão em branco. ===== */}
      <div className="ledger-grid">
        {colunasMosaico.map((coluna, i) => (
          <div className="ledger-column" key={i}>
            {coluna.map((bloco, j) => (
              <LedgerBlock
                key={bloco.titulo}
                titulo={bloco.titulo}
                linhas={bloco.linhas}
                fill={j === coluna.length - 1}
              />
            ))}
          </div>
        ))}
      </div>

      {/* ===== Entradas PLCom — Detalhamento por CFOP (tabela full-width, igual à planilha) ===== */}
      <div className="section-title">
        <FiTruck />
        <h4>Entradas PLCom — Detalhamento por CFOP</h4>
      </div>
      <RateTable
        columns={[
          { key: "cfop", label: "CFOP" },
          { key: "descricao", label: "Descrição" },
          { key: "valor", label: "Valor", type: "currency", align: "right" },
          { key: "icms", label: "ICMS", type: "currency", align: "right" },
          { key: "icmsSt", label: "ICMS ST", type: "currency", align: "right" },
          { key: "bcPisCofins", label: "BC PIS/COFINS", type: "currency", align: "right" },
          { key: "pis", label: "PIS", type: "currency", align: "right" },
          { key: "cofins", label: "COFINS", type: "currency", align: "right" },
          { key: "ipi", label: "IPI", type: "currency", align: "right" },
        ]}
        rows={entradasPorCfop}
      />

      <div className="section-title">
        <GoTable />
        <h4>Fonte</h4>
      </div>
      <div className="info-banner">
        <div className="banner-icon"><FiInfo /></div>
        <div className="banner-text">
          <span>Dados mock extraídos da aba "IMPOSTOS" de <code>MOVIMENTAÇÕES plcom teste.xlsx</code> — substituir por integração real quando o fluxo de apuração do PLCom estiver disponível.</span>
        </div>
      </div>
    </>
  );
}
